/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/**
  This file is used for controlling the global states of the components,
  you can customize the states for the different components here.
*/

import { createContext, useContext, useReducer, useMemo } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

import colors from "assets/theme/base/colors";
import dark_colors from "assets/theme-dark/base/colors";

// Material Dashboard 2 React main context
const MaterialUI = createContext();
const sio_theme_json = "theme_sio";
// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

// Material Dashboard 2 React reducer
function reducer(state, action) {
  let temp_state = {};
  switch (action.type) {
    case "MINI_SIDENAV": {
      temp_state = { ...state, miniSidenav: action.value };
      break;
    }
    case "TRANSPARENT_SIDENAV": {
      temp_state = { ...state, transparentSidenav: action.value };
      break;
    }
    case "WHITE_SIDENAV": {
      temp_state = { ...state, whiteSidenav: action.value };
      break;
    }
    case "SIDENAV_COLOR": {
      temp_state = { ...state, sidenavColor: action.value };
      break;
    }
    case "TRANSPARENT_NAVBAR": {
      temp_state = { ...state, transparentNavbar: action.value };
      break;
    }
    case "FIXED_NAVBAR": {
      temp_state = { ...state, fixedNavbar: action.value };
      break;
    }
    case "OPEN_CONFIGURATOR": {
      temp_state = { ...state, openConfigurator: action.value };
      break;
    }
    case "DIRECTION": {
      temp_state = { ...state, direction: action.value };
      break;
    }
    case "LAYOUT": {
      temp_state = { ...state, layout: action.value };
      break;
    }
    case "DARKMODE": {
      temp_state = { ...state, darkMode: action.value };
      break;
    }
    case "GET_COLOR_MODE": {
      temp_state = { ...state };
      //Write Logic to get the color
      break;
    }
    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
  window.localStorage.setItem(sio_theme_json, JSON.stringify(temp_state));
  return temp_state;
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: false,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: false,
    ...(window.localStorage.getItem(sio_theme_json) ? JSON.parse(window.localStorage.getItem(sio_theme_json)) : {})
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });

const getColor = (isDark, color, shade = "main") => {
  if (isDark) {
    return dark_colors[color][shade];
  }
  else {
    return colors[color][shade];
  }
};


export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  getColor
};
